<script>
import {defineComponent} from 'vue'
import MobileTopBar from "@js/components/TopBar/MobileTopBar.vue";
import Breadcrumbs from "@js/components/Breadcrumbs/Breadcrumbs.vue";
import {useCallbackStore} from "@js/store/CallbackStore.js";

export default defineComponent({
    name: "HowToHandOverPage",
    components: {
        MobileTopBar,
        Breadcrumbs,
    },

    data() {
        return {
            steps: [
                {
                    id: '01',
                    title: 'Свяжитесь с нами',
                    text: 'Позвоните, напишите в мессенджер или оставьте заявку на сайте. Расскажите, какой металл хотите сдать и в каком он объёме.',
                },
                {
                    id: '02',
                    title: 'Отправьте фото или описание',
                    text: 'Фото помогают быстрее понять тип металла, объём, состояние и подобрать подходящий вариант: самостоятельная сдача, вывоз, погрузка или спецтехника.',
                },
                {
                    id: '03',
                    title: 'Согласуйте формат',
                    text: 'Мы уточним адрес, условия на объекте, возможность подъезда, необходимость погрузки или демонтажа и предложим удобный сценарий сдачи.',
                },
                {
                    id: '04',
                    title: 'Сдайте металл удобным способом',
                    text: 'Вы либо привозите металл самостоятельно, либо мы организуем бесплатный вывоз и забираем лом с вашего адреса.',
                },
            ],

            formats: [
                {
                    title: 'Самостоятельно привезти в пункт приёма',
                    text: 'Подходит для небольших партий металлолома, если вам удобно доставить его самостоятельно.',
                },
                {
                    title: 'Заказать бесплатный вывоз',
                    text: 'Хороший вариант для среднего и большого объёма, когда удобнее, чтобы транспорт приехал на ваш адрес.',
                },
                {
                    title: 'Сдать лом с погрузкой',
                    text: 'Если металл тяжёлый, неудобный или его сложно перемещать вручную, можно организовать погрузку.',
                },
                {
                    title: 'Сдать металл после демонтажа',
                    text: 'Если есть старые конструкции, трубы, балки, оборудование или узлы, сначала можно выполнить демонтаж, а затем вывоз.',
                },
            ],

            checklist: [
                'Тип металла: черный, цветной, кабель, аккумуляторы, нержавейка, электродвигатели и т.д.',
                'Примерный объём или вес',
                'Адрес, если нужен вывоз',
                'Фотографии металлолома',
                'Нужна ли погрузка',
                'Нужен ли демонтаж или резка',
                'Есть ли ограничения по подъезду или территории',
            ],

            tips: [
                'Если не знаете точный вес — достаточно примерной оценки и фото.',
                'Если металл смешанный — это нормально, просто покажите состав на фото.',
                'Если лом тяжёлый или габаритный — заранее сообщите об этом менеджеру.',
                'Если объект закрытый — лучше заранее подготовить информацию по пропуску или въезду.',
            ],

            faq: [
                {
                    question: 'Нужно ли заранее сортировать металл?',
                    answer: 'Нет, это не обязательно. Если металл смешанный, просто отправьте фото или краткое описание — мы подскажем, как лучше его сдать.',
                },
                {
                    question: 'Можно ли сдать небольшой объём?',
                    answer: 'Да, можно. Небольшой объём можно привезти самостоятельно или уточнить возможность вывоза в зависимости от типа металла и адреса.',
                },
                {
                    question: 'Вы работаете только с организациями?',
                    answer: 'Нет, мы работаем и с частными клиентами, и с предприятиями, складами, стройками и производственными объектами.',
                },
                {
                    question: 'Можно ли сдать металл с выездом?',
                    answer: 'Да, конечно. Если объём и формат подходят, организуем бесплатный вывоз с вашего адреса.',
                },
                {
                    question: 'Что ускоряет оформление заявки?',
                    answer: 'Лучше всего сразу отправить фото, адрес, примерный объём и написать, нужна ли погрузка или демонтаж.',
                },
                {
                    question: 'Что делать, если я не понимаю, какой формат мне подходит?',
                    answer: 'Просто отправьте фото и краткое описание. Мы сами подскажем, как удобнее: привезти самостоятельно, заказать вывоз или использовать спецтехнику.',
                },
            ]
        }
    },
    setup(){
        const callbackStore = useCallbackStore()

        return {callbackStore}
    }
})
</script>

<template>
    <MobileTopBar/>
    <div class="container">
        <Breadcrumbs/>

        <!-- TOP INTRO -->
        <section class="mb-5 md:mb-8">
            <div class="grid grid-cols-1 xl:grid-cols-[1.1fr_.9fr] gap-5">
                <div class="bg-white border border-gray-200 rounded-[28px] p-5 md:p-8 shadow-sm">
                    <div class="text-sm uppercase tracking-[0.18em] text-gray-500 mb-3">
                        Инструкция для клиента
                    </div>

                    <h1 class="text-2xl md:text-4xl font-semibold leading-tight mb-4">
                        Как сдать металлолом
                    </h1>

                    <p class="text-gray-700 leading-relaxed mb-5 max-w-3xl">
                        Мы постарались сделать процесс максимально простым и понятным.
                        Вам не нужно разбираться в транспорте, логистике и форматах приёма —
                        достаточно связаться с нами, показать металл и коротко описать ситуацию.
                        Мы подскажем, как удобнее его сдать.
                    </p>

                    <div class="flex flex-wrap gap-2">
                        <div class="rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-sm text-gray-700">
                            Частные лица
                        </div>
                        <div class="rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-sm text-gray-700">
                            Организации
                        </div>
                        <div class="rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-sm text-gray-700">
                            Самостоятельная сдача
                        </div>
                        <div class="rounded-full border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700">
                            Бесплатный вывоз
                        </div>
                    </div>
                </div>

                <div class="rounded-[28px] border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-5 md:p-8 shadow-sm">
                    <div class="text-sm uppercase tracking-[0.18em] text-gray-500 mb-4">
                        Коротко
                    </div>

                    <div class="space-y-4">
                        <div class="pb-4 border-b border-gray-200">
                            <div class="text-xs text-baseColor font-medium mb-1">Шаг 1</div>
                            <div class="text-lg font-semibold text-gray-900">Пишите или звоните</div>
                        </div>

                        <div class="pb-4 border-b border-gray-200">
                            <div class="text-xs text-baseColor font-medium mb-1">Шаг 2</div>
                            <div class="text-lg font-semibold text-gray-900">Показываете металл</div>
                        </div>

                        <div class="pb-4 border-b border-gray-200">
                            <div class="text-xs text-baseColor font-medium mb-1">Шаг 3</div>
                            <div class="text-lg font-semibold text-gray-900">Согласовываем формат</div>
                        </div>

                        <div>
                            <div class="text-xs text-baseColor font-medium mb-1">Шаг 4</div>
                            <div class="text-lg font-semibold text-gray-900">Сдаёте удобным способом</div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- TIMELINE -->
        <section class="mb-5 md:mb-8">
            <div class="bg-grayBg border border-gray-200 rounded-[28px] p-5 md:p-8">
                <div class="flex items-end justify-between gap-4 mb-6">
                    <div>
                        <h2 class="text-xl md:text-2xl font-semibold mb-2">
                            Пошагово: как это происходит
                        </h2>
                        <p class="text-sm text-gray-600">
                            Ниже — простой порядок действий от первого сообщения до сдачи металла.
                        </p>
                    </div>
                </div>

                <div class="space-y-4 md:space-y-5">
                    <div
                        v-for="step in steps"
                        :key="step.id"
                        class="grid grid-cols-1 md:grid-cols-[96px_1fr] gap-4 items-start rounded-3xl bg-white border border-gray-200 p-4 md:p-5 shadow-sm"
                    >
                        <div
                            class="rounded-2xl bg-baseColor/10 text-baseColor border border-baseColor/20 px-4 py-5 text-2xl font-semibold text-center">
                            {{ step.id }}
                        </div>

                        <div class="pt-1">
                            <h3 class="text-lg md:text-xl font-semibold mb-2">
                                {{ step.title }}
                            </h3>
                            <p class="text-gray-700 leading-relaxed">
                                {{ step.text }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- TWO COLUMNS -->
        <section class="mb-5 md:mb-8">
            <div class="grid grid-cols-1 xl:grid-cols-2 gap-5">
                <div class="bg-white border border-gray-200 rounded-[28px] p-5 md:p-8 shadow-sm">
                    <h2 class="text-xl md:text-2xl font-semibold mb-5">
                        Как можно сдать металл
                    </h2>

                    <div class="space-y-4">
                        <div
                            v-for="item in formats"
                            :key="item.title"
                            class="rounded-2xl border border-gray-200 bg-gray-50 p-4"
                        >
                            <h3 class="font-semibold mb-2">
                                {{ item.title }}
                            </h3>
                            <p class="text-sm text-gray-700 leading-relaxed">
                                {{ item.text }}
                            </p>
                        </div>
                    </div>
                </div>

                <div class="bg-white border border-gray-200 rounded-[28px] p-5 md:p-8 shadow-sm">
                    <h2 class="text-xl md:text-2xl font-semibold mb-5">
                        Что лучше сообщить заранее
                    </h2>

                    <div class="space-y-3">
                        <div
                            v-for="item in checklist"
                            :key="item"
                            class="flex items-start gap-3 rounded-2xl border border-gray-200 p-4"
                        >
                            <div class="w-6 h-6 rounded-full bg-baseColor text-white flex items-center justify-center text-xs font-semibold shrink-0">
                                ✓
                            </div>
                            <div class="text-gray-700 leading-relaxed">
                                {{ item }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- TIPS STRIP -->
        <section class="mb-5 md:mb-8">
            <div class="rounded-[28px] border border-gray-200 bg-gradient-to-r from-gray-50 to-white p-5 md:p-8">
                <div class="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
                    <div>
                        <div class="text-sm uppercase tracking-[0.18em] text-gray-500 mb-3">
                            Полезно знать
                        </div>
                        <h2 class="text-xl md:text-2xl font-semibold leading-tight">
                            Несколько советов перед сдачей металла
                        </h2>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div
                            v-for="tip in tips"
                            :key="tip"
                            class="rounded-2xl bg-white border border-gray-200 p-4 shadow-sm"
                        >
                            <p class="text-gray-700 leading-relaxed text-sm md:text-base">
                                {{ tip }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- INFO BLOCK -->
        <section class="mb-5 md:mb-8">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div class="rounded-[28px] bg-white border border-gray-200 p-5 shadow-sm">
                    <div class="text-sm text-gray-500 mb-2">Для небольшого объёма</div>
                    <div class="text-lg font-semibold mb-2">Можно привезти самостоятельно</div>
                    <p class="text-sm text-gray-600 leading-relaxed">
                        Удобный вариант, если металла немного и вам проще доставить его в пункт приёма.
                    </p>
                </div>

                <div class="rounded-[28px] bg-white border border-green-200 p-5 shadow-sm">
                    <div class="text-sm text-green-700 mb-2">Для среднего и большого объёма</div>
                    <div class="text-lg font-semibold mb-2">Можно заказать бесплатный вывоз</div>
                    <p class="text-sm text-gray-700 leading-relaxed">
                        Если металлолома много или он находится на объекте, мы подскажем удобный формат вывоза.
                    </p>
                </div>

                <div class="rounded-[28px] bg-white border border-gray-200 p-5 shadow-sm">
                    <div class="text-sm text-gray-500 mb-2">Для сложных задач</div>
                    <div class="text-lg font-semibold mb-2">Есть погрузка и спецтехника</div>
                    <p class="text-sm text-gray-600 leading-relaxed">
                        Для тяжёлых конструкций, оборудования и крупного лома можно организовать подходящую технику.
                    </p>
                </div>
            </div>
        </section>

        <!-- FAQ -->
        <section class="mb-8 md:mb-12">
            <div class="bg-white border border-gray-200 rounded-[28px] p-5 md:p-8 shadow-sm">
                <div class="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-5">
                    <div>
                        <div class="text-sm uppercase tracking-[0.18em] text-gray-500 mb-2">
                            FAQ
                        </div>
                        <h2 class="text-xl md:text-2xl font-semibold">
                            Частые вопросы
                        </h2>
                    </div>
                </div>

                <div class="divide-y divide-gray-200">
                    <div
                        v-for="faqItem in faq"
                        :key="faqItem.question"
                        class="py-4"
                    >
                        <h3 class="font-semibold mb-2 text-base md:text-lg">
                            {{ faqItem.question }}
                        </h3>
                        <p class="text-gray-700 leading-relaxed text-sm md:text-base">
                            {{ faqItem.answer }}
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <!-- CTA -->
        <section class="mb-8 md:mb-12">
            <div class="rounded-[28px] bg-baseColor text-white p-5 md:p-8">
                <div class="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-5 items-center">
                    <div>
                        <h2 class="text-2xl md:text-3xl font-semibold mb-3">
                            Не уверены, как лучше сдать именно ваш металл?
                        </h2>
                        <p class="text-white/85 leading-relaxed max-w-2xl">
                            Просто отправьте фото, адрес и краткое описание.
                            Мы подскажем самый удобный вариант: самостоятельная сдача,
                            бесплатный вывоз, погрузка или спецтехника.
                        </p>
                    </div>

                    <div class="flex flex-col sm:flex-row gap-3">
                        <button
                            @click.prevent="callbackStore.showBaseCallbackPopup?.()"
                            type="button"
                            class="rounded-2xl bg-white text-black px-5 py-3 font-medium hover:opacity-95 transition"
                        >
                            Отправить фото
                        </button>
                        <button
                            @click.prevent="callbackStore.showBaseCallbackPopup?.({
                                title: 'Обратный звонок',
                                bitrix_title: 'Заказ обратного звонка с сайта Вторчермет-1',
                                })"
                            type="button"
                            class="rounded-2xl border border-white/30 bg-white/10 text-white px-5 py-3 font-medium hover:bg-white/15 transition"
                        >
                            Оставить заявку
                        </button>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<style scoped>
</style>
